export const COOKIE_CONSENT_STORAGE_KEY = "cc-cookie-consent";
export const COOKIE_CONSENT_VERSION = 1;

export const COOKIE_CONSENT_CHANGE_EVENT = "cc-cookie-consent-change";
export const COOKIE_CONSENT_OPEN_EVENT = "cc-cookie-consent-open";

export type CookieConsentOpenDetail = {
  /** When true, open directly to the manage panel (default: true). */
  manage?: boolean;
};

export type CookieConsentCategory = "necessary" | "analytics";

export type CookieConsentCategories = {
  necessary: true;
  analytics: boolean;
};

export type CookieConsentRecord = {
  version: number;
  categories: CookieConsentCategories;
  decidedAt: string;
};

/** Only display state crosses the server/client boundary; the browser owns the decision record. */
export type CookieConsentInitialState = Pick<CookieConsentRecord, "categories">;
export function cookieConsentInitialState(record: CookieConsentRecord | null): CookieConsentInitialState | null {
  return record ? { categories: { necessary: true, analytics: record.categories.analytics } } : null;
}

function isRecord(value: unknown): value is CookieConsentRecord {
  if (!value || typeof value !== "object") return false;
  const record = value as CookieConsentRecord;
  return (
    record.version === COOKIE_CONSENT_VERSION &&
    record.categories?.necessary === true &&
    typeof record.categories.analytics === "boolean" &&
    typeof record.decidedAt === "string"
  );
}

/** Shared validation for the server cookie and legacy localStorage record. */
export function parseCookieConsent(raw?: string): CookieConsentRecord | null {
  if (!raw) return null;
  try { const parsed: unknown = JSON.parse(raw); return isRecord(parsed) ? parsed : null; }
  catch { return null; }
}

export function readCookieConsent(): CookieConsentRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = parseCookieConsent(window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY) ?? undefined);
    if (stored) return stored;
  } catch { /* The first-party cookie still works when localStorage is unavailable. */ }
  try {
    const raw = document.cookie.split("; ").find(row => row.startsWith(`${COOKIE_CONSENT_STORAGE_KEY}=`));
    return parseCookieConsent(raw ? decodeURIComponent(raw.slice(COOKIE_CONSENT_STORAGE_KEY.length + 1)) : undefined);
  } catch { return null; }
}

export function mirrorCookieConsent(record: CookieConsentRecord): void {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_CONSENT_STORAGE_KEY}=${encodeURIComponent(JSON.stringify(record))}; Path=/; Max-Age=31536000; SameSite=Lax${window.location.protocol === "https:" ? "; Secure" : ""}`;
}

export function writeCookieConsent(
  categories: Pick<CookieConsentCategories, "analytics">,
): CookieConsentRecord {
  const record: CookieConsentRecord = {
    version: COOKIE_CONSENT_VERSION,
    categories: {
      necessary: true,
      analytics: categories.analytics,
    },
    decidedAt: new Date().toISOString(),
  };

  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(record));
    } catch { /* Do not prevent saving the cookie or notifying trackers. */ }
    mirrorCookieConsent(record);
    window.dispatchEvent(
      new CustomEvent(COOKIE_CONSENT_CHANGE_EVENT, { detail: record.categories }),
    );
  }

  return record;
}

export function hasCookieConsent(category: CookieConsentCategory): boolean {
  const stored = readCookieConsent();
  if (!stored) return category === "necessary";
  if (category === "necessary") return true;
  return stored.categories[category] === true;
}

export function clearCookieConsentForTesting(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
  document.cookie = `${COOKIE_CONSENT_STORAGE_KEY}=; Path=/; Max-Age=0`;
}

/** Re-open the site cookie banner / preferences UI (e.g. from the privacy policy). */
export function openCookiePreferences(detail: CookieConsentOpenDetail = {}): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(COOKIE_CONSENT_OPEN_EVENT, {
      detail: { manage: detail.manage ?? true },
    }),
  );
}
